import React from 'react'
import { Swords, RefreshCw, Users } from 'lucide-react'
import { PotatoPiratesSimulator } from '@/components/potatoPirates/PotatoPiratesSimulator'
import { makeMultiPlayerState } from '@/components/potatoPirates/engine'

const MIN_PLAYERS = 2
const MAX_PLAYERS = 6

export function PotatoPiratesSandbox() {
  const [playerCount, setPlayerCount] = React.useState(2)
  const [gameKey, setGameKey] = React.useState(0)
  const initialState = React.useMemo(() => makeMultiPlayerState(playerCount), [gameKey, playerCount])

  const handleNewGame = () => setGameKey((k) => k + 1)

  const handlePlayerCountChange = (count: number) => {
    setPlayerCount(count)
    setGameKey((k) => k + 1) // reset board when player count changes
  }

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-3">
        <span className="w-10 h-10 rounded-xl bg-orange-500/15 text-orange-500 flex items-center justify-center">
          <Swords className="w-5 h-5" />
        </span>
        <div>
          <h1 className="text-xl font-bold text-[var(--color-text)]">Potato Pirates — Sandbox</h1>
          <p className="text-sm text-[var(--color-text-muted)]">
            Each captain programs their ships, then all fleets battle in turn order.
          </p>
        </div>

        {/* Player count + new game controls */}
        <div className="ml-auto flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-[var(--color-text-muted)]" />
            <span className="text-sm font-semibold text-[var(--color-text-muted)]">Players:</span>
            <div className="flex gap-1">
              {Array.from({ length: MAX_PLAYERS - MIN_PLAYERS + 1 }, (_, i) => i + MIN_PLAYERS).map((n) => (
                <button
                  key={n}
                  onClick={() => handlePlayerCountChange(n)}
                  className={`w-8 h-8 rounded-lg text-sm font-bold transition-colors ${
                    playerCount === n
                      ? 'bg-orange-500 text-white'
                      : 'bg-[var(--color-surface-hover)] text-[var(--color-text-muted)] hover:bg-[var(--color-surface-deep)]'
                  }`}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>
          <button
            onClick={handleNewGame}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-[var(--color-border)] text-sm font-semibold text-[var(--color-text)] hover:bg-[var(--color-surface-hover)]"
          >
            <RefreshCw className="w-4 h-4" /> New Game
          </button>
        </div>
      </div>

      {/* How-to strip */}
      <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4 mb-4 text-xs text-[var(--color-text-muted)] leading-relaxed">
        <strong className="text-[var(--color-text)]">How to play:</strong>{' '}
        Each ship holds up to 3 cards. <strong>Roast</strong> = −1, <strong>Mash</strong> = −2, <strong>Fry</strong> = −3 potatoes.
        Use <strong>For</strong> / <strong>While</strong> to repeat attacks, or <strong>If-Else</strong> to hit every enemy ship (skips your own) based on their potato count.
        In multi-player, use the target dropdown on each action card to choose which player to hit.
        Fleets execute in turn order (Player 1, 2, … {playerCount}). Last fleet standing wins.
      </div>

      {/* Simulator */}
      <div className="bg-[var(--color-surface)] rounded-xl border border-[var(--color-border)] p-4">
        <PotatoPiratesSimulator key={gameKey} initialState={initialState} />
      </div>
    </div>
  )
}

export default PotatoPiratesSandbox
